import { type AchievementEvent, type AchievementId, type Profile } from '../shared/achievements';

interface Transport { profile: (signal?:AbortSignal) => Promise<Profile>; action: (event: AchievementEvent) => Promise<Profile> }
interface Observer { profile: (profile: Profile) => void; earned: (ids: AchievementId[]) => void; error: (message: string) => void }
const eventAward: Record<AchievementEvent, AchievementId> = {'wall-draw':'first-draw','wall-stamp':'first-stamp','wall-image':'first-image','wall-cutout':'cut-above','tool-spray':'tool-spray','tool-marker':'tool-marker','tool-pen':'tool-pen','tool-shapes':'tool-shapes','tool-fill':'tool-fill','tool-eraser':'tool-eraser'};
interface ActionCohort {events:AchievementEvent[]; earned:Set<AchievementId>}

/** Serialize snapshots and action reports within one authenticated identity.
 * Failed cosmetic reports stay pending; replacing identity discards this client. */
export class AchievementClient {
  private active = true;
  private current: Profile | null = null;
  private pending = new Set<AchievementEvent>();
  private cohorts:ActionCohort[]=[];
  private openCohort:ActionCohort|null=null;
  private chain: Promise<void> = Promise.resolve();
  private initialRefresh: Promise<void> | null = null;
  private baseline:Promise<void>|null=null;
  private baselineAbort:AbortController|null=null;
  constructor(private transport: Transport, private observer: Observer) {}
  close() { this.active = false; this.baselineAbort?.abort(); this.pending.clear(); this.cohorts=[]; this.openCohort=null; }
  /** Establish old awards before a save can change them. Stats are advisory:
   * a failed or stalled read must never prevent saving the artwork itself. */
  async beforeMutation() {
    try { await this.loadBaseline(); }
    catch { if(this.active)this.observer.error('Your stats could not refresh. Your artwork is safe. Try again to sync your progress.'); }
  }
  private loadBaseline() {
    if(this.current||!this.active)return Promise.resolve();
    if(this.baseline)return this.baseline;
    const controller=new AbortController();this.baselineAbort=controller;
    let timer:ReturnType<typeof setTimeout>;
    const deadline=new Promise<Profile>((_,reject)=>{
      controller.signal.addEventListener('abort',()=>reject(new Error('Achievement baseline interrupted.')),{once:true});
      timer=setTimeout(()=>controller.abort(),1500);
    });
    const pending=Promise.race([Promise.resolve().then(()=>this.transport.profile(controller.signal)),deadline]).then(profile=>{
      if(!this.current)this.accept(profile,false);
    }).finally(()=>{
      clearTimeout(timer);
      if(this.baseline===pending)this.baseline=null;
      if(this.baselineAbort===controller)this.baselineAbort=null;
    });
    this.baseline=pending;
    return pending;
  }
  async flush() {
    await this.refresh();
    if (this.active && this.pending.size) throw new Error('Reconnect and try again so your guest achievements can follow your account. Your progress and draft are safe.');
  }
  record(event: AchievementEvent) {
    if (!this.active || this.pending.has(event) || this.current?.achievements.some(award => award.id === eventAward[event])) return this.chain;
    this.pending.add(event);
    if(!this.openCohort){
      const cohort:ActionCohort={events:[],earned:new Set()};
      this.openCohort=cohort;this.cohorts.push(cohort);
      // Only synchronous reports from one gesture share a celebration. Seal
      // before network waits so a later gesture never joins an in-flight one.
      void Promise.resolve().then(()=>{if(this.openCohort===cohort)this.openCohort=null;});
    }
    this.openCohort.events.push(event);
    return this.refresh();
  }
  refresh() {
    // Identity creation and the first connection can request the same silent
    // baseline. Action cohorts and later reconnects still need their own work.
    if (!this.current && !this.cohorts.length && this.initialRefresh) return this.initialRefresh;
    const initial = !this.current && !this.cohorts.length;
    this.chain = this.chain.then(async () => {
      if (!this.active) return;
      const cohorts=this.cohorts.slice();
      this.openCohort=null;
      try {
        // A silent baseline prevents celebrating old awards on reload or login.
        const needsBaseline = !this.current;
        if (needsBaseline) await this.loadBaseline();
        const hadPending = cohorts.length > 0;
        for(const cohort of cohorts){
          while(cohort.events.length){
            if(!this.active)return;
            const event=cohort.events[0];
            const earned=this.accept(await this.transport.action(event),false);
            if(!this.active)return;
            for(const id of earned)cohort.earned.add(id);
            this.pending.delete(event);cohort.events.shift();
          }
          this.cohorts.shift();
          if(cohort.earned.size)this.observer.earned([...cohort.earned]);
        }
        if (this.active && !needsBaseline && !hadPending) this.accept(await this.transport.profile(), true);
        if (this.active) this.observer.error('');
      } catch {
        if (this.active) this.observer.error('Your stats could not refresh. Your artwork is safe. Try again to sync your progress.');
      }
    });
    if (initial) {
      const pending = this.chain;
      this.initialRefresh = pending;
      const clear = () => { if (this.initialRefresh === pending) this.initialRefresh = null; };
      void pending.then(clear, clear);
    }
    return this.chain;
  }
  private accept(profile: Profile, celebrate: boolean) {
    if (!this.active) return [];
    const previous = new Set(this.current?.achievements.map(award => award.id));
    const newlyEarned = profile.achievements.filter(award => !previous.has(award.id)).map(award => award.id);
    this.current = profile;
    this.observer.profile(profile);
    if (celebrate && newlyEarned.length) this.observer.earned(newlyEarned);
    return newlyEarned;
  }
}
